import { getImageProps, type ImageProps } from 'next/image'

export interface ResponsivePictureSource {
  src: string | null | undefined
  media: string
  sizes?: string
}

export interface ResponsivePictureImageProps {
  alt: string
  sources: ResponsivePictureSource[]
  fallbackSrc: string | null | undefined
  fallbackSizes?: string
  priority?: boolean
  loading?: 'eager' | 'lazy'
  fetchPriority?: 'high' | 'low' | 'auto'
  className?: string
  fill?: boolean
  quality?: ImageProps['quality']
}

const DEFAULT_SIZES = '100vw'

export const ResponsivePictureImage = (props: ResponsivePictureImageProps) => {
  const {
    alt,
    sources,
    fallbackSrc,
    fallbackSizes = DEFAULT_SIZES,
    priority = false,
    loading,
    fetchPriority,
    className,
    fill = false,
    quality,
  } = props

  if (!fallbackSrc) return null

  const resolvedLoading: 'eager' | 'lazy' | undefined = priority ? 'eager' : loading
  const resolvedFetchPriority: 'high' | 'low' | 'auto' | undefined = fetchPriority ?? (priority ? 'high' : undefined)

  // Runs the URL through Next's image pipeline (loader, srcSet, sizes, style)
  // so we can spread the result onto a plain <img> or pluck srcSet/sizes for <source>.
  const getOptimizedImgAttrs = ({ src, sizes }: { src: string, sizes: string }) => {
    const { props: imgAttrs } = getImageProps({
      src,
      alt,
      fill,
      sizes,
      priority,
      loading: resolvedLoading,
      quality,
    })
    return imgAttrs
  }

  const fallbackAttrs = getOptimizedImgAttrs({ src: fallbackSrc, sizes: fallbackSizes })

  return (
    <picture className={className}>
      {sources.map((source) => {
        if (!source.src) return null
        const { srcSet, sizes } = getOptimizedImgAttrs({ src: source.src, sizes: source.sizes ?? DEFAULT_SIZES })
        return (
          <source
            key={source.media}
            media={source.media}
            srcSet={srcSet}
            sizes={sizes}
          />
        )
      })}
      <img
        {...fallbackAttrs}
        alt={alt}
        fetchPriority={resolvedFetchPriority}
      />
    </picture>
  )
}
